/**
 * The sprite loader (integration plan slice 3; the loader spec the sprites
 * repo's `BACKLOG.md` rows 11 and 16 ask for, written here, not lifted: no
 * prototype reads a contract export). From one export (`SpriteSource`: the
 * JSON of `contracts/monster.md` §5, validated by `spriteExportSchema`, and
 * its PNG):
 *
 * 1. **Repack.** The export PNG is one row of frames (the golem's is 13,536 px
 *    wide, past every phone's texture limit); `frames` gives each frame's
 *    top-left and is the only layout truth. At load it is redrawn into a
 *    square-ish atlas on an offscreen canvas: ceil(√frames) columns, fewer if
 *    that would pass `presentation.sprite.atlasMax` on a side. The golem's 141
 *    96-px frames become 12 × 12 cells, 1152² texels. The atlas's pixels are
 *    kept as `ImageData` (CPU memory, which a GPU reset cannot drop, unlike an
 *    accelerated canvas): the copy a lost context rebuilds the texture from.
 *    Neither the decoded PNG nor the canvas is kept. A sheet without a usable
 *    `idle` (one drawn facing `s`, the fallback of every other anim) is
 *    refused here, before it is installed (`spriteExportSchema` refuses it
 *    first; this guards a source that skipped it).
 * 2. **Frame table.** Each frame's rectangle in the atlas in UV units (u, v of
 *    its top-left, width, height), for a texture uploaded with `flipY` false.
 * 3. **Facing table.** For each of game-core's eight directions, the drawn
 *    facing and whether to flip it, by the contract's three steps, always from
 *    the export's `directions` and `mirror` (never a fixed list; per-character
 *    extra facings need no change here): the direction itself if drawn; else
 *    its `mirror` source, flipped, if that source is drawn; else the nearest
 *    direction the first two steps resolve, by angle. A tie (two candidates
 *    45° away) goes to the one nearer the horizontal axis, because the
 *    contract says a prototype set (`s e n`) uses `e` for `se` and `ne` (so `w`,
 *    `e` flipped, for `sw` and `nw`), as the prototype's `sprite-facing.js`
 *    did (a diagonal draws the profile row); then to the one that needs no
 *    flip, then the clockwise one.
 * 4. **Anim table.** `anims[name].dirs[drawn]`: frames, their `ms`, `loop` and
 *    `hitFrame`; an anim drawn without directions (`faint`, `sleep`) keys `s`
 *    and never flips. An anim the sheet does not have falls back to `idle`.
 * 5. **World size.** The plane is `cell / (pxPerMetre × tileMetres)` tiles on
 *    each side (`presentation.sprite.tileMetres`); the pivot (the feet row,
 *    `cell.h − 3`) sits on the tile's centre, its bottom edge on the ground,
 *    and x at the pivot's column. Never scaled to fix a size (contract §1).
 */
import type { SpriteExport, SpriteSource } from '@ab/content-schema';
import { type Dir, dirs } from '@ab/game-core';
import * as THREE from 'three';
import { presentation } from './constants.ts';

export type Facing = { readonly drawn: Dir; readonly flip: boolean };

/** One anim in one drawn facing. `frames` index the sheet's frame table. */
export type AnimRun = {
  readonly frames: readonly number[];
  readonly ms: readonly number[];
  /** The sum of `ms`. */
  readonly total: number;
  readonly loop: boolean;
  readonly hitFrame: number | null;
};

/** What a unit draws for an anim and a game facing: the run, the anim actually used (after the idle fallback) and the flip. */
export type Resolved = { readonly name: string; readonly run: AnimRun; readonly drawn: Dir; readonly flip: boolean };

export type SpriteSheet = {
  /** The entity id (`monster.golem`). */
  readonly id: string;
  /** The repacked atlas's pixels: the CPU copy every texture of this sheet is uploaded from. */
  readonly atlas: ImageData;
  readonly columns: number;
  readonly rows: number;
  /** Four floats per frame: u and v of its top-left, its width and height, in UV units of the atlas. */
  readonly rects: Float32Array;
  readonly facings: Readonly<Record<Dir, Facing>>;
  /** The plane's size in tiles, and the pivot: from the plane's left edge (x) and bottom edge (y), in tiles. */
  readonly width: number;
  readonly height: number;
  readonly pivotX: number;
  readonly pivotY: number;
  has(anim: string): boolean;
  /** The anim for a game facing, or `idle` when the sheet does not draw it. */
  resolve(anim: string, facing: Dir): Resolved;
  /** A texture of the atlas (nearest filtering, no mipmaps, sRGB, `flipY` false); the caller owns and disposes it. */
  texture(): THREE.Texture;
};

/** Clockwise index of each direction from north (45° steps). */
const clockwise: Readonly<Record<Dir, number>> = { n: 0, ne: 1, e: 2, se: 3, s: 4, sw: 5, w: 6, nw: 7 };
const steps = (a: Dir, b: Dir): number => {
  const d = Math.abs(clockwise[a] - clockwise[b]) % 8;
  return Math.min(d, 8 - d);
};
/** Steps from the horizontal axis: 0 for e and w, 1 for the diagonals, 2 for n and s. */
const fromHorizontal = (d: Dir): number => Math.min(steps(d, 'e'), steps(d, 'w'));

/** The contract's three-step facing resolution (module comment, step 3). */
export const resolveFacings = (directions: SpriteExport['directions'], mirror: SpriteExport['mirror']): Readonly<Record<Dir, Facing>> => {
  const drawn = new Set<string>(directions);
  const direct = (d: Dir): Facing | null => {
    if (drawn.has(d)) return { drawn: d, flip: false };
    const source = d === 'sw' || d === 'w' || d === 'nw' ? mirror[d] : undefined;
    return source !== undefined && drawn.has(source) ? { drawn: source, flip: true } : null;
  };
  const out = {} as Record<Dir, Facing>;
  for (const d of dirs) {
    const own = direct(d);
    if (own !== null) {
      out[d] = own;
      continue;
    }
    const candidates = dirs.flatMap((c) => {
      const f = direct(c);
      return f === null ? [] : [{ c, f }];
    });
    candidates.sort(
      (a, b) =>
        steps(d, a.c) - steps(d, b.c) ||
        fromHorizontal(a.c) - fromHorizontal(b.c) ||
        Number(a.f.flip) - Number(b.f.flip) ||
        ((clockwise[a.c] - clockwise[d] + 8) % 8) - ((clockwise[b.c] - clockwise[d] + 8) % 8),
    );
    const best = candidates[0];
    // The schema requires s, e and n for a body sprite, so there is always a candidate.
    if (best === undefined) throw new Error('a sprite sheet with no drawn facing');
    out[d] = best.f;
  }
  return out;
};

/** Atlas columns and rows for `count` cells of `cell` px, within `max` texels on a side. */
export const atlasLayout = (count: number, cell: readonly [number, number], max: number): { readonly columns: number; readonly rows: number } => {
  const columns = Math.max(1, Math.min(Math.ceil(Math.sqrt(count)), Math.floor(max / cell[0])));
  const rows = Math.ceil(count / columns);
  if (rows * cell[1] > max) throw new Error(`${count} frames of ${cell[0]}×${cell[1]} do not fit a ${max}² atlas`);
  return { columns, rows };
};

const buildSheet = (source: SpriteSource, image: ImageBitmap): SpriteSheet => {
  const meta = source.meta;
  const [w, h] = meta.cell;
  const pivot = meta.pivot;
  if (pivot === null) throw new Error(`${source.id}: a sprite without a pivot cannot stand on a tile`);
  if (meta.anims['idle']?.dirs.s === undefined) throw new Error(`${source.id}: the sheet has no idle drawn facing s (every missing anim falls back to it)`);
  const { columns, rows } = atlasLayout(meta.frames.length, meta.cell, presentation.sprite.atlasMax);
  const canvas = new OffscreenCanvas(columns * w, rows * h);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) throw new Error('no 2D canvas context to repack a sprite sheet');
  ctx.imageSmoothingEnabled = false;
  const rects = new Float32Array(meta.frames.length * 4);
  meta.frames.forEach(([fx, fy], i) => {
    const x = (i % columns) * w;
    const y = Math.floor(i / columns) * h;
    ctx.drawImage(image, fx, fy, w, h, x, y, w, h);
    rects.set([x / canvas.width, y / canvas.height, w / canvas.width, h / canvas.height], i * 4);
  });
  const atlas = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const tile = meta.pxPerMetre * presentation.sprite.tileMetres;
  const facings = resolveFacings(meta.directions, meta.mirror);
  const runs = new Map<string, AnimRun>();
  const runOf = (name: string, dir: Dir): AnimRun | null => {
    const anim = Object.hasOwn(meta.anims, name) ? meta.anims[name] : undefined;
    const d = anim?.dirs[dir] ?? anim?.dirs.s;
    if (anim === undefined || d === undefined) return null;
    const key = `${name}.${dir}`;
    const known = runs.get(key);
    if (known !== undefined) return known;
    const run = { frames: d.frames, ms: d.ms, total: d.ms.reduce((a, b) => a + b, 0), loop: anim.loop, hitFrame: anim.hitFrame ?? null };
    runs.set(key, run);
    return run;
  };
  const has = (name: string): boolean => Object.hasOwn(meta.anims, name);
  return {
    id: source.id,
    atlas,
    columns,
    rows,
    rects,
    facings,
    width: w / tile,
    height: h / tile,
    pivotX: pivot[0] / tile,
    pivotY: (h - pivot[1] - 1) / tile,
    has,
    resolve(anim, facing) {
      const name = has(anim) ? anim : 'idle';
      const f = facings[facing];
      const directed = meta.anims[name]?.dirs[f.drawn] !== undefined;
      const run = runOf(name, f.drawn) ?? runOf('idle', f.drawn);
      if (run === null) throw new Error(`${source.id} has no idle anim`);
      return { name, run, drawn: directed ? f.drawn : 's', flip: directed && f.flip };
    },
    texture() {
      const texture = new THREE.Texture(atlas);
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      texture.flipY = false;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.name = `sprite:${source.id}`;
      texture.needsUpdate = true;
      return texture;
    },
  };
};

/** Prepared sheets by source (the URL and the export's shape), kept for the page's life: a remounted view installs them at once. */
const ready = new Map<string, SpriteSheet>();
const pending = new Map<string, Promise<SpriteSheet>>();
const keyOf = (s: SpriteSource): string => `${s.url}#${s.meta.frames.length}:${s.meta.sheet.join('x')}:${s.meta.cell.join('x')}`;

/** A sheet already prepared this page, or undefined. */
export const preparedSheet = (source: SpriteSource): SpriteSheet | undefined => ready.get(keyOf(source));

/** Fetches (`fetch` + `createImageBitmap`) and repacks a sheet once per page; later calls share the result. */
export const loadSheet = (source: SpriteSource): Promise<SpriteSheet> => {
  const key = keyOf(source);
  const done = ready.get(key);
  if (done !== undefined) return Promise.resolve(done);
  const running = pending.get(key);
  if (running !== undefined) return running;
  const load = (async () => {
    const response = await fetch(source.url);
    if (!response.ok) throw new Error(`sprite ${source.id}: ${source.url} answered ${response.status}`);
    const image = await createImageBitmap(await response.blob());
    try {
      const sheet = buildSheet(source, image);
      ready.set(key, sheet);
      return sheet;
    } finally {
      image.close();
      pending.delete(key);
    }
  })();
  pending.set(key, load);
  load.catch(() => pending.delete(key));
  return load;
};
